/**
 * Escalas de calificación — ESPEJO display-only del core Rust (F3-B,
 * `core-rust/src/grading/scheme.rs`). El servidor es la AUTORIDAD de la nota
 * (gradebook ponderado + conversión); este registro solo REPRODUCE la
 * presentación para la UI (libro de notas, expediente, certificado).
 *
 * Contrato numérico (idéntico a Rust):
 *   • `convert(raw)` — nota cruda (fracción 0..1) → valor nativo persistido
 *     (ve-0-20 → 0..20; el resto → 0..100 como porcentaje portador).
 *   • `displayValue(value)` — renderiza un valor NATIVO ya persistido
 *     (lo que guarda `cohort_enrollment.final_grade` / `certificate.final_grade`).
 *   • `toDisplay(raw)` = `displayValue(convert(raw))`.
 *
 * Cada escala se auto-describe con un `FeatureManifest` (`academy.grading.<key>`)
 * que alimenta el catálogo de features (`pnpm gen:features`).
 */

import type { FeatureManifest } from "../country/packs";

export type GradingSchemeKey = "ve-0-20" | "pct-100" | "letter" | "pass-fail";

export interface GradingSchemeStrategy {
  readonly key: GradingSchemeKey;
  /** Máximo de la escala nativa (20 para ve-0-20; 100 el resto). */
  readonly maxValue: number;
  /** Umbral de aprobado como FRACCIÓN cruda 0..1. */
  readonly passingThresholdPct: number;
  /** Nombre legible corto (para selects/UI). */
  readonly label: string;
  /** Convierte una nota cruda (0..1) al valor nativo persistible. */
  convert(raw: number): number;
  /** Renderiza un valor nativo ya persistido. */
  displayValue(value: number): string;
  /** Cadena humana directa desde la nota cruda (0..1). */
  toDisplay(raw: number): string;
  /** ¿Aprueba con esta nota cruda (0..1)? */
  isPassing(raw: number): boolean;
}

const clamp01 = (x: number): number => Math.max(0, Math.min(1, x));
const roundTo = (x: number, n: number): number => {
  const f = 10 ** n;
  return Math.round(x * f) / f;
};

/** Fábrica base: comparte convert/toDisplay/isPassing en términos de displayValue. */
function makeScheme(
  base: Pick<GradingSchemeStrategy, "key" | "maxValue" | "passingThresholdPct" | "label"> & {
    displayValue(value: number): string;
  },
): GradingSchemeStrategy {
  return {
    ...base,
    convert(raw: number) {
      return clamp01(raw) * base.maxValue;
    },
    toDisplay(raw: number) {
      return base.displayValue(clamp01(raw) * base.maxValue);
    },
    isPassing(raw: number) {
      return clamp01(raw) + Number.EPSILON >= base.passingThresholdPct;
    },
  };
}

/** Letra A–F a partir de un porcentaje 0..100 (== `Letter::letter_for` en Rust). */
export function letterFor(pct: number): string {
  if (pct >= 90) {
    return "A";
  }
  if (pct >= 80) {
    return "B";
  }
  if (pct >= 70) {
    return "C";
  }
  if (pct >= 60) {
    return "D";
  }
  return "F";
}

const VE_0_20 = makeScheme({
  key: "ve-0-20",
  maxValue: 20,
  passingThresholdPct: 0.5,
  label: "0–20 (Venezuela)",
  displayValue(value) {
    const v = roundTo(value, 1);
    return Number.isInteger(v) ? String(Math.round(v)) : v.toFixed(1);
  },
});

const PCT_100 = makeScheme({
  key: "pct-100",
  maxValue: 100,
  passingThresholdPct: 0.6,
  label: "Porcentaje 0–100",
  displayValue(value) {
    return `${Math.round(value)}%`;
  },
});

const LETTER = makeScheme({
  key: "letter",
  maxValue: 100,
  passingThresholdPct: 0.6,
  label: "Letras A–F",
  displayValue(value) {
    return letterFor(value);
  },
});

const PASS_FAIL = makeScheme({
  key: "pass-fail",
  maxValue: 100,
  passingThresholdPct: 0.6,
  label: "Apto / No apto",
  displayValue(value) {
    return value + 1e-9 >= 60 ? "Apto" : "No apto";
  },
});

const SCHEMES: Record<GradingSchemeKey, GradingSchemeStrategy> = {
  "ve-0-20": VE_0_20,
  "pct-100": PCT_100,
  letter: LETTER,
  "pass-fail": PASS_FAIL,
};

/** Orden estable de escalas (para selects y catálogo). */
export const GRADING_SCHEME_KEYS: readonly GradingSchemeKey[] = [
  "ve-0-20",
  "pct-100",
  "letter",
  "pass-fail",
];

export const DEFAULT_GRADING_SCHEME_KEY: GradingSchemeKey = "ve-0-20";

/** Devuelve la estrategia de una clave, o el defecto `ve-0-20` si es desconocida. */
export function gradingScheme(key: string | null | undefined): GradingSchemeStrategy {
  const k = (key ?? "").trim();
  return (SCHEMES as Record<string, GradingSchemeStrategy>)[k] ?? VE_0_20;
}

function pickDefined(o?: string | null): string | undefined {
  const t = (o ?? "").trim();
  return t.length > 0 ? t : undefined;
}

/** Resuelve la escala efectiva por precedencia cohorte → curso → org → defecto. */
export function resolveGradingScheme(
  cohort?: string | null,
  course?: string | null,
  org?: string | null,
): GradingSchemeStrategy {
  return gradingScheme(pickDefined(cohort) ?? pickDefined(course) ?? pickDefined(org));
}

// ─── Modelos de calificación GUIADOS POR DATOS (grupo E) ─────────────────────
//
// Espejo display-only de `core-rust/src/grading/model.rs::ModelScheme`. La UI
// renderiza las notas y hace la PREVISUALIZACIÓN en vivo a partir de la forma de
// un `grading_model` sincronizado (max/decimales/bandas/sufijo/umbral) en vez de
// las 4 escalas hardcodeadas. Su salida es IDÉNTICA a las 4 del sistema (paridad
// verificada en tests) para no cambiar nada de lo ya mostrado.

export type GradingModelKind = "points" | "percent" | "letter" | "pass_fail";

export interface GradingLetterBand {
  label: string;
  /** % mínimo 0..100 (inclusive) que otorga esta etiqueta. */
  minPct: number;
}

/** Forma mínima de un modelo para renderizar/prever (camelCase). */
export interface GradingModelShape {
  /** Clave persistida / identidad (system_key o id del modelo). */
  schemeKey?: string;
  kind: GradingModelKind;
  maxValue: number;
  /** Umbral de aprobado como FRACCIÓN 0..1. */
  passingThreshold: number;
  decimals: number;
  letterBands?: GradingLetterBand[] | null;
  displaySuffix?: string | null;
}

/** Fila cruda `grading_model` (snake_case) tal como llega por sync. */
export interface GradingModelRow {
  id: string;
  organization_id?: string;
  name?: string;
  kind: string;
  max_value: number;
  passing_threshold: number;
  decimals?: number | null;
  letter_bands?: unknown;
  display_suffix?: string | null;
  is_system?: boolean | null;
  system_key?: string | null;
}

function parseBands(raw: unknown): GradingLetterBand[] {
  let arr: unknown = raw;
  if (typeof raw === "string") {
    try {
      arr = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(arr)) {
    return [];
  }
  return arr
    .map((b) => {
      // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- trust-boundary parse of jsonb bands
      const o = (typeof b === "object" && b !== null ? b : {}) as Record<string, unknown>;
      const rawMin = o.minPct ?? o.min_pct ?? 0;
      return {
        label: String(o.label ?? ""),
        minPct: typeof rawMin === "number" ? rawMin : Number(rawMin),
      };
    })
    .filter((b) => b.label.length > 0 && Number.isFinite(b.minPct));
}

const GRADING_MODEL_KINDS: readonly GradingModelKind[] = [
  "points",
  "percent",
  "letter",
  "pass_fail",
];

/** Coacciona una cadena a un `GradingModelKind` conocido (defecto `points`). */
export function parseGradingModelKind(kind: string): GradingModelKind {
  return GRADING_MODEL_KINDS.find((k) => k === kind) ?? "points";
}

/** Normaliza una fila `grading_model` (snake_case + jsonb) a `GradingModelShape`. */
export function modelShapeFromRow(row: GradingModelRow): GradingModelShape {
  const kind = parseGradingModelKind(row.kind);
  return {
    schemeKey: row.system_key ?? row.id,
    kind,
    maxValue: row.max_value,
    passingThreshold: row.passing_threshold,
    decimals: row.decimals ?? 0,
    letterBands: kind === "letter" ? parseBands(row.letter_bands) : null,
    displaySuffix: row.display_suffix ?? null,
  };
}

/** Estrategia display-only (no exige que la clave sea de las 4 del sistema). */
export type GradingDisplayStrategy = Omit<GradingSchemeStrategy, "key" | "label">;

function numericDisplay(value: number, decimals: number, suffix?: string | null): string {
  const dec = Math.max(0, Math.trunc(decimals));
  const v = roundTo(value, dec);
  const body = Number.isInteger(v) ? String(Math.round(v)) : v.toFixed(dec);
  return suffix ? `${body}${suffix}` : body;
}

/** Construye una estrategia display-only desde la forma de un modelo. */
export function modelStrategy(shape: GradingModelShape): GradingDisplayStrategy {
  const displayValue = (value: number): string => {
    switch (shape.kind) {
      case "letter": {
        const bands = shape.letterBands ?? [];
        for (const b of bands) {
          if (value >= b.minPct) {
            return b.label;
          }
        }
        return bands.at(-1)?.label ?? "";
      }
      case "pass_fail": {
        return value + 1e-9 >= shape.passingThreshold * shape.maxValue ? "Apto" : "No apto";
      }
      default: {
        return numericDisplay(value, shape.decimals, shape.displaySuffix);
      }
    }
  };
  return {
    maxValue: shape.maxValue,
    passingThresholdPct: shape.passingThreshold,
    convert(raw) {
      return clamp01(raw) * shape.maxValue;
    },
    displayValue,
    toDisplay(raw) {
      return displayValue(clamp01(raw) * shape.maxValue);
    },
    isPassing(raw) {
      return clamp01(raw) + Number.EPSILON >= shape.passingThreshold;
    },
  };
}

/** Las 4 formas del sistema (espejo de `SystemModelSpec::ALL` en Rust). */
export function systemModelShapes(): Record<GradingSchemeKey, GradingModelShape> {
  const letterBands: GradingLetterBand[] = [
    { label: "A", minPct: 90 },
    { label: "B", minPct: 80 },
    { label: "C", minPct: 70 },
    { label: "D", minPct: 60 },
    { label: "F", minPct: 0 },
  ];
  return {
    "ve-0-20": {
      schemeKey: "ve-0-20",
      kind: "points",
      maxValue: 20,
      passingThreshold: 0.5,
      decimals: 1,
    },
    "pct-100": {
      schemeKey: "pct-100",
      kind: "percent",
      maxValue: 100,
      passingThreshold: 0.6,
      decimals: 0,
      displaySuffix: "%",
    },
    letter: {
      schemeKey: "letter",
      kind: "letter",
      maxValue: 100,
      passingThreshold: 0.6,
      decimals: 0,
      letterBands,
    },
    "pass-fail": {
      schemeKey: "pass-fail",
      kind: "pass_fail",
      maxValue: 100,
      passingThreshold: 0.6,
      decimals: 0,
    },
  };
}

/** Manifests auto-descriptivos de las escalas (catálogo de features). */
export function gradingSchemeManifests(): FeatureManifest[] {
  const descriptions: Record<GradingSchemeKey, string> = {
    "ve-0-20": "Escala venezolana 0–20 (aprobado con 10). Escala por defecto de la academia.",
    "pct-100": "Porcentaje 0–100 (aprobado con 60%).",
    letter: "Calificación por letras A–F (aprobado con D = 60%).",
    "pass-fail": "Apto / No apto (aprobado con 60%).",
  };
  return GRADING_SCHEME_KEYS.map((key) => ({
    key: `academy.grading.${key}`,
    name: SCHEMES[key].label,
    description: descriptions[key],
    scope: "org" as const,
    status: "stable" as const,
  }));
}
